import Conversationstarterscard from './conversationstarterscard';
import { Message, RootState } from '@circuitry-ai/doc-data';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardBody } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { getUserInitials, getUserName, ID_CONSTANTS, ErrorBoundary } from '@circuitry-ai/doc-ui';
import CommonChatMessages, { ChatMetaData } from './common-chat-messages';
import { ChatAdvisorDetails } from './chat-advisor-details';

import { ChatSkeletonLoader } from './chat-skeleton-loader';
import ConverseDisclaimer from './converse-disclaimer';
import ChatForm from './chatForm';
import { useSelector } from 'react-redux';
import TipsAndTricks from './tips-and-tricks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  enableChatAutoScroll,
  scrollChatToBottom,
  updateChatAutoScrollState,
} from './chatAutoScroll';

/* eslint-disable-next-line */
export interface ChatuiwidgetProps {
  messages: Message[];
  handleSendClick: (userInput: string) => void;
  currentActiveSocketObject: object;
  converseSenderId?: string;
  showDisclaimer: boolean;
  isStreamingInProgress?: boolean;
  onDeepThinkRegenerate?: () => void;
  commandFormDataRef?: React.MutableRefObject<{ formData: Record<string, string>; customMessage: string } | null>;
  /** Optional content rendered above the live chat thread (e.g. prior conversation-log messages when continuing a conversation). */
  leadingContent?: React.ReactNode;
  /** When true, hides the advisor intro card and conversation starters (used when continuing an existing conversation). */
  hideIntro?: boolean;
  /** Content rendered at the top of the message scroller (e.g. advisor chrome + sticky tabs). */
  scrollHeader?: React.ReactNode;
  /**
   * Conversations panel, rendered below the header and beside the complete
   * thread/composer column.
   */
  conversationsSidebar?: React.ReactNode;
  /** Controls shown above the conversation starters and beside the panel. */
  conversationActions?: React.ReactNode;
  /** PAL view: limits the `/` agent list to agents enabled for PAL. */
  palView?: boolean;
}

// Hoist ErrorBoundary-wrapped components to module scope to preserve identity across renders
const ConversationstarterscardWithErrorBoundary = ErrorBoundary(Conversationstarterscard);
const CommonChatMessagesWithErrorBoundary = ErrorBoundary(CommonChatMessages);
const ChatSkeletonLoaderWithErrorBoundary = ErrorBoundary(ChatSkeletonLoader);
const TipsAndTricksWithErrorBoundary = ErrorBoundary(TipsAndTricks);
const ChatFormWithErrorBoundary = ErrorBoundary(ChatForm);
const ConverseDisclaimerWithErrorBoundary = ErrorBoundary(ConverseDisclaimer);
const ChatAdvisorDetailsWithErrorBoundary = ErrorBoundary(ChatAdvisorDetails);

export function ChatUIExpand(props: Readonly<ChatuiwidgetProps>) {
  const {
    messages,
    handleSendClick,
    currentActiveSocketObject,
    converseSenderId,
    isStreamingInProgress = false,
    onDeepThinkRegenerate
  } = props;

  const [isSpeechMode, setIsSpeechMode] = useState<boolean>(false);
  const [isVoiceWebCallActive, setIsVoiceWebCallActive] = useState(false);
  const [voiceTranscriptLines, setVoiceTranscriptLines] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const voiceTranscriptEndRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null); //to scroll end of page
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasStreamingMessageRef = useRef<boolean>(false);
  const knowledgeSourceTextRef = useRef<HTMLParagraphElement | null>(null);
  const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
  const activeChatAdvisor = useSelector((state: RootState) => state.advisor.activeChatAdvisor);
  const { isSocketLoading } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);

  // components with error boundary (hoisted at module scope)

  // Track if there's a streaming message
  const hasStreamingMessage = messages.some(msg => msg.isStreaming === true);
  hasStreamingMessageRef.current = hasStreamingMessage;
  const isInputDisabled = hasStreamingMessage || isSocketLoading || isStreamingInProgress;
  const streamingMessage = hasStreamingMessage
    ? messages.find((msg) => msg.isStreaming)
    : undefined;
  const streamingMessageContent = streamingMessage?.message ?? '';
  const streamingThinkingStepCount = streamingMessage?.thinkingProcesses?.length ?? 0;

  // Re-enable follow-bottom when the user sends a new message or streaming starts.
  useEffect(() => {
    if (isSocketLoading || isStreamingInProgress || hasStreamingMessage) {
      enableChatAutoScroll();
    }
  }, [isSocketLoading, isStreamingInProgress, hasStreamingMessage]);

  // Scroll to skeleton loader (thinking text) when a new message is sent
  useEffect(() => {
    if (isSocketLoading || isStreamingInProgress) {
      enableChatAutoScroll();
      scrollTimeoutRef.current = setTimeout(() => {
        scrollChatToBottom('smooth', true);
      }, 100);
    }
  }, [messages.length, isSocketLoading, isStreamingInProgress]);

  // Keep the bottom anchor in view while the advisor response is still streaming.
  useEffect(() => {
    if (!hasStreamingMessage) return;
    const frameId = requestAnimationFrame(() => {
      scrollChatToBottom('auto');
    });
    return () => cancelAnimationFrame(frameId);
  }, [streamingMessageContent, streamingThinkingStepCount, hasStreamingMessage]);

  // Keep follow-bottom in sync whether `#scroll-container` or a parent page scroller moves.
  useEffect(() => {
    const handleScroll = (event: Event) => {
      updateChatAutoScrollState(event);
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const userInitial = getUserInitials(user?.nickname);
  const advisorUserName = activeChatAdvisor?.name ?? 'Advisor';
  const userName = getUserName(isAuthenticated, user);
  const advisorLogo = activeChatAdvisor?.assets?.[0]?.s3_path;
  const tipsAndTricksEnabled = activeChatAdvisor?.tips_and_tricks?.trim() !== '' && activeChatAdvisor?.tips_and_tricks !== null;

  const renderMessages = useCallback((msg: Message, msgIndex: number) => {
    const messageId = msg.isAdvisor ? `advisor-response-${msg?.metaData?.[6]?.message_id || 'greet'}` : `advisor-query-${msgIndex}`;
    const isAgentMessage = !!(msg as any).agentName;
    const messageData: ChatMetaData = {
      isAdvisor: msg.isAdvisor,
      initials: userInitial,
      advisorLogo: advisorLogo,
      username: msg.isAdvisor ? (isAgentMessage ? (msg as any).agentName : advisorUserName) : userName,
      textMessage: msg?.message?.trim() ?? '',
      viewKnowledge: msg.isAdvisor && msg?.metaData?.[1]?.source_ids?.length > 0,
      viewExpertAnswer: msg.isAdvisor && msg?.metaData?.[5]?.rephrased_query,
      messageId: messageId,
      figure_refs: msg?.metaData?.[8]?.figure_refs || [],
      isStreaming: msg.isStreaming, // Pass isStreaming status to message rendering
      isAgent: isAgentMessage,
      agentName: (msg as any).agentName
    };
    // Find the index of the last advisor message
    const lastAdvisorIndex = [...messages].map((m, i) => m.isAdvisor ? i : -1).filter(i => i !== -1).pop();
    const isLatestAdvisorMessage = msg.isAdvisor && msgIndex === lastAdvisorIndex;
    return <CommonChatMessagesWithErrorBoundary 
      chatAdvisor={activeChatAdvisor} 
      isAdvisorView={true} 
      expandView={true} 
      chatMetaData={messageData} 
      isSpeechMode={isSpeechMode} 
      key={msgIndex} 
      msgIndex={msgIndex} 
      msg={msg} 
      setIsSpeechMode={setIsSpeechMode}
      onDeepThinkRegenerate={onDeepThinkRegenerate}
      isLatestAdvisorMessage={isLatestAdvisorMessage}
      converseSenderId={converseSenderId}
      ref={isLatestAdvisorMessage ? knowledgeSourceTextRef : null}
    />;
  }, [userInitial, advisorLogo, advisorUserName, userName, isSpeechMode, activeChatAdvisor, onDeepThinkRegenerate, messages, converseSenderId]);

  const normalizeConversationStarters = (value: unknown): string[] => {
    if (Array.isArray(value) && value.length <=3) {
      return value;
    }
    return ['', '', ''];
  };

  const handleVoiceAgentConnectionChange = useCallback((connected: boolean) => {
    setIsVoiceWebCallActive(connected);
    if (!connected) setVoiceTranscriptLines([]);
  }, []);

  const handleVoiceAgentTranscriptLine = useCallback((line: { role: 'user' | 'assistant'; text: string }) => {
    setVoiceTranscriptLines((prev) => [...prev, line]);
  }, []);

  useEffect(() => {
    if (!isVoiceWebCallActive || voiceTranscriptLines.length === 0) return;
    const id = requestAnimationFrame(() => {
      voiceTranscriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
    return () => cancelAnimationFrame(id);
  }, [voiceTranscriptLines.length, isVoiceWebCallActive]);

  const chatFormProps = {
    handleSendClick,
    isSpeechMode,
    setIsSpeechMode,
    messagesLength: messages.length,
    disabled: isInputDisabled,
    onVoiceAgentConnectionChange: handleVoiceAgentConnectionChange,
    onVoiceAgentTranscriptLine: handleVoiceAgentTranscriptLine,
    commandFormDataRef: props.commandFormDataRef,
    palView: props.palView,
  };

  return (
    <div id={ID_CONSTANTS.sessionId + (currentActiveSocketObject as any)?.id} className="flex flex-col h-full min-h-0 w-full">
      <div className="flex min-h-0 flex-1">
        {props.conversationsSidebar}
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            id='scroll-container'
            className='flex-1 min-h-0 overflow-y-auto'
            onScroll={updateChatAutoScrollState}
          >
            {props.scrollHeader}
            <div className='flex flex-col gap-4 px-2'>
              {props.conversationActions}
              {isVoiceWebCallActive ? (
                <Card shadow="sm" className="min-h-[200px] flex-1 border border-success/20 bg-content1">
                  <CardBody className="flex max-h-[min(70vh,560px)] flex-col gap-3 overflow-hidden p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-default-500">{t('Live transcription')}</p>
                    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
                      {voiceTranscriptLines.length === 0 ? (
                        <p className="text-sm text-default-400">{t('Waiting for speech…')}</p>
                      ) : (
                        voiceTranscriptLines.map((line, i) => (
                          <div
                            key={`${i}-${line.text.slice(0, 32)}`}
                            className={
                              line.role === 'user'
                                ? 'ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-default-800 px-4 py-2 text-sm text-white'
                                : 'mr-auto max-w-[85%] rounded-2xl rounded-bl-sm border border-default-200 bg-default-100 px-4 py-2 text-sm text-default-800'
                            }
                          >
                            {line.role === 'user' ? line.text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{line.text}</ReactMarkdown>}
                          </div>
                        ))
                      )}
                      <div ref={voiceTranscriptEndRef} />
                    </div>
                  </CardBody>
                </Card>
              ) : (
                <>
                  {props.leadingContent}
                  {!props.hideIntro && (
                    <>
                      <ChatAdvisorDetailsWithErrorBoundary description={activeChatAdvisor?.description} />
                      <ConversationstarterscardWithErrorBoundary data={normalizeConversationStarters(activeChatAdvisor?.conversation_starters)} onHandleClick={handleSendClick} />
                    </>
                  )}
                  {messages?.map((msg: Message, msgIndex: number) => renderMessages(msg, msgIndex))}
                  <ChatSkeletonLoaderWithErrorBoundary isStreamingInProgress={hasStreamingMessage || isStreamingInProgress} />
                  <div ref={endOfMessagesRef} />
                </>
              )}
            </div>
          </div>
          <div className='px-2 flex flex-col gap-2 shrink-0'>
            {tipsAndTricksEnabled && !isVoiceWebCallActive && !props.hideIntro && <TipsAndTricksWithErrorBoundary disabled={isInputDisabled} />}
            <ChatFormWithErrorBoundary {...chatFormProps} />
          </div>
          <div className="shrink-0">
            <ConverseDisclaimerWithErrorBoundary showDisclaimer={props.showDisclaimer} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatUIExpand;